import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useRazorpay from 'react-razorpay';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

// Add animation styles
const modalStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out;
  }
`;

const StudentPackagesPage = () => {
  const navigate = useNavigate();
  const [Razorpay] = useRazorpay();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [userBalance, setUserBalance] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchasedPackage, setPurchasedPackage] = useState(null);
  const session = SessionManager.getCurrentSession();

  useEffect(() => {
    if (!session || session.role !== 'candidate') {
      navigate('/login');
      return;
    }
    loadPackages();
    loadUserBalance();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/packages/plans`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserBalance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/packages/balance/${session.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setUserBalance(data);
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const handlePurchasePackage = async (packagePlan) => {
    // Re-check session before payment
    const candidateSession = SessionManager.getSession('candidate');
    const currentCandidateId = candidateSession?.user_id;
    const currentToken = candidateSession?.access_token;
    
    console.log('🔐 Package purchase auth check:', {
      hasCandidateId: !!currentCandidateId,
      hasToken: !!currentToken
    });
    
    if (!currentCandidateId || !currentToken) {
      console.log('❌ Session expired, redirecting to login');
      alert('Your session has expired. Please login to continue');
      navigate('/login');
      return;
    }

    setPaymentLoading(true);

    try {
      console.log('💳 Creating package order...');
      const response = await fetch(`${API_BASE_URL}/packages/create-order`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          candidate_id: currentCandidateId,
          package_plan_id: packagePlan.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment order');
      }

      const orderData = await response.json();
      console.log('✅ Package order created:', orderData);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || orderData.razorpay_key_id || 'rzp_live_S9Mi0lChtnAtHt',
        amount: orderData.amount_inr * 100,
        currency: 'INR',
        name: 'Aptirix',
        description: `${packagePlan.plan_name} Package`,
        order_id: orderData.order_id,
        prefill: {
          name: candidateSession.profile?.first_name || candidateSession.email?.split('@')[0] || 'User',
          email: candidateSession.email,
          contact: candidateSession.profile?.phone || ''
        },
        notes: {
          package_plan_id: packagePlan.id,
          candidate_id: currentCandidateId,
          plan_name: packagePlan.plan_name
        },
        theme: {
          color: '#2b6cee'
        },
        handler: async function (response) {
          try {
            console.log('✅ [PAYMENT] Payment successful, verifying...');
            console.log('Payment response:', response);
            
            const verifyResponse = await fetch(`${API_BASE_URL}/packages/verify-payment`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${currentToken}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                candidate_id: currentCandidateId,
                package_plan_id: packagePlan.id
              })
            });

            console.log('✅ [PAYMENT] Verification response status:', verifyResponse.status);

            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              console.log('✅ [PAYMENT] Payment verified successfully:', verifyData);
              
              // Reload user balance
              await loadUserBalance();
              
              // Set purchased package for success modal
              setPurchasedPackage(packagePlan);
              setShowSuccessModal(true);
            } else {
              const errorText = await verifyResponse.text();
              console.error('Payment verification failed:', errorText);
              alert('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            console.log('Payment modal dismissed');
            setPaymentLoading(false);
          },
        },
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const calculateSavings = (pkg) => {
    // Calculate individual credit cost vs package cost
    const individualCost = (
      pkg.ai_trainer_credits * 50 +
      pkg.enhance_cv_credits * 30 +
      pkg.job_apply_credits * 20 +
      pkg.placement_training_credits * 40
    );
    const savings = individualCost - pkg.price_inr;
    const percentage = Math.round((savings / individualCost) * 100);
    return { savings, percentage };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LandingHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading packages...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <style>{modalStyles}</style>
      <LandingHeader />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white overflow-hidden py-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
            STUDENT PACKAGES
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Boost Your Career with Package Deals
          </h1>
          <p className="text-base sm:text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Get bundled credits for AI training, CV enhancement, job applications, and placement training at discounted prices
          </p>
        </div>
      </section>

      {/* Current Balance Section */}
      {userBalance && (
        <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Your Current Balance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <Icon name="psychology" className="text-3xl text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-blue-600">{userBalance.ai_trainer_balance}</p>
                  <p className="text-xs text-gray-600">AI Trainer</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <Icon name="description" className="text-3xl text-green-600 mb-2" />
                  <p className="text-2xl font-bold text-green-600">{userBalance.enhance_cv_balance}</p>
                  <p className="text-xs text-gray-600">CV Enhance</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <Icon name="work" className="text-3xl text-purple-600 mb-2" />
                  <p className="text-2xl font-bold text-purple-600">{userBalance.job_apply_balance}</p>
                  <p className="text-xs text-gray-600">Job Apply</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <Icon name="school" className="text-3xl text-orange-600 mb-2" />
                  <p className="text-2xl font-bold text-orange-600">{userBalance.placement_training_balance}</p>
                  <p className="text-xs text-gray-600">Placement Training</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Package Cards */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => {
              const { savings, percentage } = calculateSavings(pkg);
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-white rounded-2xl p-6 shadow-md transition-all duration-300 flex flex-col group ${
                    pkg.badge
                      ? 'border-2 border-[#2b6cee] shadow-xl hover:shadow-2xl hover:-translate-y-2'
                      : 'border border-gray-200 hover:shadow-xl hover:border-[#2b6cee] hover:-translate-y-2'
                  }`}
                >
                  {pkg.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] text-white text-xs font-semibold rounded-full shadow-md">
                      {pkg.badge}
                    </div>
                  )}

                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{pkg.plan_name}</h3>
                    <p className="text-xs text-gray-600 mb-4">{pkg.description}</p>
                    
                    <div className="mb-1">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatPrice(pkg.price_inr)}
                      </span>
                    </div>
                    
                    {pkg.discount_percentage > 0 && (
                      <div className="text-xs text-green-600 font-semibold">
                        Save {pkg.discount_percentage}% • ₹{savings.toLocaleString('en-IN')} off
                      </div>
                    )}
                    
                    <div className="mt-2 text-xs text-gray-500">
                      Valid for {pkg.validity_days} days
                    </div>
                  </div>

                  {/* Credits Included */}
                  <div className="space-y-2 mb-4 flex-grow">
                    <div className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded-lg">
                      <span className="text-gray-700">AI Trainer</span>
                      <span className="font-bold text-blue-600">{pkg.ai_trainer_credits} credits</span>
                    </div>
                    <div className="flex items-center justify-between text-xs bg-green-50 p-2 rounded-lg">
                      <span className="text-gray-700">CV Enhance</span>
                      <span className="font-bold text-green-600">{pkg.enhance_cv_credits} credits</span>
                    </div>
                    <div className="flex items-center justify-between text-xs bg-purple-50 p-2 rounded-lg">
                      <span className="text-gray-700">Job Apply</span>
                      <span className="font-bold text-purple-600">{pkg.job_apply_credits} credits</span>
                    </div>
                    <div className="flex items-center justify-between text-xs bg-orange-50 p-2 rounded-lg">
                      <span className="text-gray-700">Placement Training</span>
                      <span className="font-bold text-orange-600">{pkg.placement_training_credits} credits</span>
                    </div>
                  </div>

                  {/* Features */}
                  {showAllFeatures && pkg.features && (
                    <ul className="space-y-2 mb-4">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Icon name="check_circle" size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    onClick={() => handlePurchasePackage(pkg)}
                    disabled={paymentLoading}
                    variant={pkg.badge ? 'primary' : 'outline'}
                    size="sm"
                    className={`w-full text-sm transition-all duration-300 ${
                      pkg.badge
                        ? 'bg-gradient-to-r from-[#2b6cee] to-[#1a4fb8] text-white hover:shadow-lg group-hover:scale-105'
                        : 'group-hover:scale-105'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {paymentLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Processing...
                      </div>
                    ) : (
                      'Purchase Package'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-[#2b6cee] hover:text-[#1a4fb8] bg-white border-2 border-[#2b6cee] hover:border-[#1a4fb8] rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              {showAllFeatures ? (
                <>
                  <Icon name="expand_less" size={20} />
                  Show Less Features
                </>
              ) : (
                <>
                  <Icon name="expand_more" size={20} />
                  Compare All Features
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Individual Credits Option */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Prefer Pay-As-You-Go?
          </h2>
          <p className="text-gray-600 mb-6">
            You can also purchase individual credits for specific services
          </p>
          <Button
            onClick={() => navigate('/candidate/credits')}
            variant="outline"
            size="lg"
            className="inline-flex items-center gap-2"
          >
            <Icon name="account_balance_wallet" size={20} />
            Buy Individual Credits
          </Button>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && purchasedPackage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            {/* Success Message */}
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
              Package Purchased!
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Congratulations! You have successfully purchased the <span className="font-semibold text-gray-900">{purchasedPackage.plan_name}</span> package.
            </p>
            
            {/* Credits Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="card_giftcard" className="text-blue-600 text-2xl mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Credits Added</p>
                  <div className="space-y-1 text-sm text-blue-700">
                    {purchasedPackage.ai_trainer_credits > 0 && (
                      <p>• AI Trainer: {purchasedPackage.ai_trainer_credits} credits</p>
                    )}
                    {purchasedPackage.enhance_cv_credits > 0 && (
                      <p>• CV Enhance: {purchasedPackage.enhance_cv_credits} credits</p>
                    )}
                    {purchasedPackage.job_apply_credits > 0 && (
                      <p>• Job Apply: {purchasedPackage.job_apply_credits} credits</p>
                    )}
                    {purchasedPackage.placement_training_credits > 0 && (
                      <p>• Placement Training: {purchasedPackage.placement_training_credits} credits</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Validity Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="schedule" className="text-green-600 text-2xl mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-1">Package Validity</p>
                  <p className="text-sm text-green-700">
                    Your credits are valid for {purchasedPackage.validity_days} days from today.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/candidate/dashboard');
                }}
                className="w-full bg-[#2b6cee] hover:bg-[#1a4fb8] text-white py-3 text-base font-semibold rounded-xl"
              >
                Go to Dashboard
              </Button>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  window.location.reload();
                }}
                className="w-full text-gray-600 hover:text-gray-900 py-2 text-sm font-medium transition"
              >
                View More Packages
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudentPackagesPage;
